/**
 * Lifecycle Dashboard Widgets
 * 
 * Comprehensive dashboard components for the Workflow Lifecycle Overview page.
 * These widgets provide real-time insights into workflow instances, jobs, timeline events,
 * and execution results in a unified dashboard view.
 */

import {
    ActionIcon,
    Alert,
    Badge,
    Button,
    Card,
    Grid,
    Group,
    Progress,
    RingProgress,
    ScrollArea,
    Stack,
    Text,
    Timeline,
    Title,
    Tooltip
} from '@mantine/core';
import '@mantine/core/styles.css';
import {
    IconActivity,
    IconAlertTriangle,
    IconArrowRight,
    IconChartBar,
    IconCheck,
    IconChevronRight,
    IconClock,
    IconCpu,
    IconDatabase,
    IconEye,
    IconFlame,
    IconGitBranch,
    IconPlayerPlay,
    IconRefresh,
    IconRocket,
    IconTimeline,
    IconTrendingUp,
    IconX
} from '@tabler/icons-react';
import React, { useCallback, useEffect, useState } from 'react';
import { notifications } from '@mantine/notifications';

// Dashboard Widget Props
interface DashboardWidgetProps {
    title: string;
    icon: React.ReactNode;
    children: React.ReactNode;
    actions?: React.ReactNode;
    loading?: boolean;
    error?: string;
    height?: number | string;
}

// Reusable Dashboard Widget Container
const DashboardWidget: React.FC<DashboardWidgetProps> = ({
    title,
    icon,
    children,
    actions,
    loading,
    error,
    height = 'auto'
}) => {
    return (
        <Card withBorder style={{ height }}>
            <Group justify="space-between" mb="md">
                <Group gap="xs">
                    {icon}
                    <Title order={5}>{title}</Title>
                </Group>
                {actions}
            </Group>
            
            {error ? (
                <Alert color="red" icon={<IconAlertTriangle size={16} />}>
                    {error}
                </Alert>
            ) : loading ? (
                <Stack ta="center" gap="md" style={{ minHeight: 100 }}>
                    <Progress value={30} animated size="sm" style={{ width: '100%' }} />
                    <Text size="sm" c="dimmed">Loading...</Text>
                </Stack>
            ) : (
                children
            )}
        </Card>
    );
};

// Workflow Overview Summary Widget
export const WorkflowOverviewWidget: React.FC<{
    data?: {
        total_instances: number;
        running_instances: number;
        completed_instances: number;
        failed_instances: number;
        success_rate: number;
        avg_completion_time: number;
    };
    onViewAll?: () => void;
}> = ({ data, onViewAll }) => {
    const mockData = data || {
        total_instances: 156,
        running_instances: 8,
        completed_instances: 132,
        failed_instances: 16,
        success_rate: 89,
        avg_completion_time: 2.4
    };

    return (
        <DashboardWidget
            title="Workflow Overview"
            icon={<IconRocket size={20} />}
            actions={
                <Button
                    variant="subtle"
                    size="xs"
                    rightSection={<IconChevronRight size={14} />}
                    onClick={onViewAll}
                >
                    View All
                </Button>
            }
        >
            <Grid>
                <Grid.Col span={6}>
                    <RingProgress
                        size={120}
                        thickness={8}
                        sections={[
                            { value: (mockData.running_instances / mockData.total_instances) * 100, color: 'blue' },
                            { value: (mockData.completed_instances / mockData.total_instances) * 100, color: 'green' },
                            { value: (mockData.failed_instances / mockData.total_instances) * 100, color: 'red' }
                        ]}
                        label={
                            <Text size="xs" ta="center" fw={700}>
                                {mockData.total_instances}
                                <br />
                                <Text size="xs" c="dimmed">Total</Text>
                            </Text>
                        }
                    />
                </Grid.Col>
                <Grid.Col span={6}>
                    <Stack gap="xs">
                        <Group justify="space-between">
                            <Group gap="xs">
                                <Badge size="xs" color="blue">●</Badge>
                                <Text size="sm">Running</Text>
                            </Group>
                            <Text size="sm" fw={500}>{mockData.running_instances}</Text>
                        </Group>
                        <Group justify="space-between">
                            <Group gap="xs">
                                <Badge size="xs" color="green">●</Badge>
                                <Text size="sm">Completed</Text>
                            </Group>
                            <Text size="sm" fw={500}>{mockData.completed_instances}</Text>
                        </Group>
                        <Group justify="space-between">
                            <Group gap="xs">
                                <Badge size="xs" color="red">●</Badge>
                                <Text size="sm">Failed</Text>
                            </Group>
                            <Text size="sm" fw={500}>{mockData.failed_instances}</Text>
                        </Group>
                        <div style={{ borderTop: '1px solid #e9ecef', paddingTop: '8px', marginTop: '8px' }}>
                            <Group justify="space-between">
                                <Text size="sm" c="dimmed">Success Rate</Text>
                                <Text size="sm" fw={700} c="green">{mockData.success_rate}%</Text>
                            </Group>
                            <Group justify="space-between">
                                <Text size="sm" c="dimmed">Avg Time</Text>
                                <Text size="sm" fw={500}>{mockData.avg_completion_time}h</Text>
                            </Group>
                        </div>
                    </Stack>
                </Grid.Col>
            </Grid>
        </DashboardWidget>
    );
};

// Active Jobs Widget
export const ActiveJobsWidget: React.FC<{
    data?: Array<{
        id: string;
        name: string;
        status: string;
        progress: number;
        duration: number;
    }>;
    onViewAll?: () => void;
}> = ({ data, onViewAll }) => {
    const mockData = data || [
        { id: 'job_001', name: 'Data Processing', status: 'running', progress: 65, duration: 1200 },
        { id: 'job_002', name: 'Report Generation', status: 'running', progress: 30, duration: 800 },
        { id: 'job_003', name: 'Email Notifications', status: 'queued', progress: 0, duration: 0 },
        { id: 'job_004', name: 'Backup Process', status: 'running', progress: 85, duration: 2400 }
    ];

    const runningJobs = mockData.filter(job => job.status === 'running');

    return (
        <DashboardWidget
            title="Active Jobs"
            icon={<IconActivity size={20} />}
            actions={
                <Group gap="xs">
                    <Badge size="xs" color="blue">{runningJobs.length} running</Badge>
                    <Button
                        variant="subtle"
                        size="xs"
                        rightSection={<IconChevronRight size={14} />}
                        onClick={onViewAll}
                    >
                        View All
                    </Button>
                </Group>
            }
            height={300}
        >
            <ScrollArea h={200}>
                <Stack gap="md">
                    {mockData.slice(0, 4).map(job => (
                        <div key={job.id}>
                            <Group justify="space-between" mb={4}>
                                <div>
                                    <Text size="sm" fw={500}>{job.name}</Text>
                                    <Text size="xs" c="dimmed">{job.id}</Text>
                                </div>
                                <Group gap="xs">
                                    <Badge
                                        size="xs"
                                        color={
                                            job.status === 'running' ? 'blue' :
                                            job.status === 'queued' ? 'gray' :
                                            job.status === 'completed' ? 'green' : 'red'
                                        }
                                    >
                                        {job.status}
                                    </Badge>
                                    {job.status === 'running' && (
                                        <Text size="xs" c="dimmed">{Math.floor(job.duration / 1000)}s</Text>
                                    )}
                                </Group>
                            </Group>
                            <Progress
                                value={job.progress}
                                color={
                                    job.status === 'running' ? 'blue' :
                                    job.status === 'completed' ? 'green' : 'gray'
                                }
                                size="sm"
                            />
                        </div>
                    ))}
                </Stack>
            </ScrollArea>
        </DashboardWidget>
    );
};

// Recent Activity Timeline Widget
export const RecentActivityWidget: React.FC<{
    data?: Array<{
        id: string;
        action: string;
        target: string;
        timestamp: string;
        status: 'success' | 'failure' | 'warning';
        actor: string;
    }>;
    onViewAll?: () => void;
}> = ({ data, onViewAll }) => {
    const mockData = data || [
        {
            id: '1',
            action: 'Workflow Started',
            target: 'Customer Onboarding',
            timestamp: new Date(Date.now() - 300000).toISOString(),
            status: 'success' as const,
            actor: 'system'
        },
        {
            id: '2',
            action: 'State Transition',
            target: 'Approval → Processing',
            timestamp: new Date(Date.now() - 600000).toISOString(),
            status: 'success' as const,
            actor: 'user_alice'
        },
        {
            id: '3',
            action: 'Job Failed',
            target: 'Email Service',
            timestamp: new Date(Date.now() - 900000).toISOString(),
            status: 'failure' as const,
            actor: 'system'
        },
        {
            id: '4',
            action: 'Signal Received',
            target: 'External API',
            timestamp: new Date(Date.now() - 1200000).toISOString(),
            status: 'success' as const,
            actor: 'external_service'
        }
    ];

    const getActivityIcon = (status: string) => {
        switch (status) {
            case 'success': return <IconCheck size={12} />;
            case 'failure': return <IconX size={12} />;
            case 'warning': return <IconAlertTriangle size={12} />;
            default: return <IconClock size={12} />;
        }
    };

    const getActivityColor = (status: string) => {
        switch (status) {
            case 'success': return 'green';
            case 'failure': return 'red';
            case 'warning': return 'orange';
            default: return 'blue';
        }
    };

    return (
        <DashboardWidget
            title="Recent Activity"
            icon={<IconTimeline size={20} />}
            actions={
                <Button
                    variant="subtle"
                    size="xs"
                    rightSection={<IconChevronRight size={14} />}
                    onClick={onViewAll}
                >
                    View All
                </Button>
            }
            height={300}
        >
            <ScrollArea h={200}>
                <Timeline lineWidth={2}>
                    {mockData.map(activity => (
                        <Timeline.Item
                            key={activity.id}
                            bullet={getActivityIcon(activity.status)}
                            title={activity.action}
                            color={getActivityColor(activity.status)}
                        >
                            <Text size="sm" c="dimmed" mb={2}>
                                Target: <strong>{activity.target}</strong>
                            </Text>
                            <Group gap="xs">
                                <Text size="xs" c="dimmed">
                                    {new Date(activity.timestamp).toLocaleTimeString()}
                                </Text>
                                <Badge size="xs" variant="outline">
                                    {activity.actor}
                                </Badge>
                            </Group>
                        </Timeline.Item>
                    ))}
                </Timeline>
            </ScrollArea>
        </DashboardWidget>
    );
};

// System Health Widget
export const SystemHealthWidget: React.FC<{
    data?: {
        cpu_usage: number;
        memory_usage: number;
        active_workers: number;
        total_workers: number;
        system_status: 'healthy' | 'warning' | 'critical';
    };
    onViewDetails?: () => void;
}> = ({ data, onViewDetails }) => {
    const mockData = data || {
        cpu_usage: 68,
        memory_usage: 72,
        active_workers: 7,
        total_workers: 8,
        system_status: 'healthy' as const
    };

    const healthColor = {
        healthy: 'green',
        warning: 'orange',
        critical: 'red'
    }[mockData.system_status];

    const healthIcon = {
        healthy: <IconCheck size={16} />,
        warning: <IconAlertTriangle size={16} />,
        critical: <IconFlame size={16} />
    }[mockData.system_status];

    return (
        <DashboardWidget
            title="System Health"
            icon={<IconCpu size={20} />}
            actions={
                <Group gap="xs">
                    <Badge size="sm" color={healthColor} leftSection={healthIcon}>
                        {mockData.system_status.toUpperCase()}
                    </Badge>
                    <ActionIcon
                        variant="subtle"
                        size="sm"
                        onClick={onViewDetails}
                    >
                        <IconEye size={16} />
                    </ActionIcon>
                </Group>
            }
        >
            <Stack gap="md">
                <div>
                    <Group justify="space-between" mb={4}>
                        <Text size="sm">CPU Usage</Text>
                        <Text size="sm" fw={500}>{mockData.cpu_usage}%</Text>
                    </Group>
                    <Progress value={mockData.cpu_usage} color="blue" />
                </div>

                <div>
                    <Group justify="space-between" mb={4}>
                        <Text size="sm">Memory Usage</Text>
                        <Text size="sm" fw={500}>{mockData.memory_usage}%</Text>
                    </Group>
                    <Progress value={mockData.memory_usage} color="orange" />
                </div>

                <div>
                    <Group justify="space-between" mb={4}>
                        <Text size="sm">Active Workers</Text>
                        <Text size="sm" fw={500}>
                            {mockData.active_workers}/{mockData.total_workers}
                        </Text>
                    </Group>
                    <Progress
                        value={(mockData.active_workers / mockData.total_workers) * 100}
                        color="green"
                    />
                </div>

                <Group justify="space-between" style={{ 
                    borderTop: '1px solid #e9ecef', 
                    paddingTop: '12px' 
                }}>
                    <Text size="xs" c="dimmed">Last updated: {new Date().toLocaleTimeString()}</Text>
                    <ActionIcon size="sm" variant="subtle" onClick={() => window.location.reload()}>
                        <IconRefresh size={14} />
                    </ActionIcon>
                </Group>
            </Stack>
        </DashboardWidget>
    );
};

// Performance Metrics Widget
export const PerformanceMetricsWidget: React.FC<{
    data?: {
        avg_execution_time: number;
        throughput: number;
        error_rate: number;
        sla_compliance: number;
        trend: 'up' | 'down' | 'stable';
    };
    onViewDetails?: () => void;
}> = ({ data, onViewDetails }) => {
    const mockData = data || {
        avg_execution_time: 1.2,
        throughput: 45,
        error_rate: 2.3,
        sla_compliance: 97.8,
        trend: 'up' as const
    };

    const trendIcon = {
        up: <IconTrendingUp size={16} color="green" />,
        down: <IconArrowRight size={16} color="red" style={{ transform: 'rotate(45deg)' }} />,
        stable: <IconArrowRight size={16} color="blue" />
    }[mockData.trend];

    return (
        <DashboardWidget
            title="Performance Metrics"
            icon={<IconChartBar size={20} />}
            actions={
                <Group gap="xs">
                    <Tooltip label={`Performance is trending ${mockData.trend}`}>
                        {trendIcon}
                    </Tooltip>
                    <ActionIcon
                        variant="subtle"
                        size="sm"
                        onClick={onViewDetails}
                    >
                        <IconEye size={16} />
                    </ActionIcon>
                </Group>
            }
        >
            <Grid>
                <Grid.Col span={6}>
                    <div style={{ textAlign: 'center' }}>
                        <Text size="xl" fw={700} c="blue">{mockData.avg_execution_time}s</Text>
                        <Text size="xs" c="dimmed">Avg Execution Time</Text>
                    </div>
                </Grid.Col>
                <Grid.Col span={6}>
                    <div style={{ textAlign: 'center' }}>
                        <Text size="xl" fw={700} c="green">{mockData.throughput}</Text>
                        <Text size="xs" c="dimmed">Jobs/min</Text>
                    </div>
                </Grid.Col>
                <Grid.Col span={6}>
                    <div style={{ textAlign: 'center' }}>
                        <Text size="xl" fw={700} c="red">{mockData.error_rate}%</Text>
                        <Text size="xs" c="dimmed">Error Rate</Text>
                    </div>
                </Grid.Col>
                <Grid.Col span={6}>
                    <div style={{ textAlign: 'center' }}>
                        <Text size="xl" fw={700} c="violet">{mockData.sla_compliance}%</Text>
                        <Text size="xs" c="dimmed">SLA Compliance</Text>
                    </div>
                </Grid.Col>
            </Grid>

            <div style={{ marginTop: '16px', textAlign: 'center' }}>
                <Progress
                    value={mockData.sla_compliance}
                    color={mockData.sla_compliance > 95 ? 'green' : mockData.sla_compliance > 90 ? 'orange' : 'red'}
                    size="sm"
                />
                <Text size="xs" c="dimmed" mt={4}>
                    SLA Target: 95% • Current: {mockData.sla_compliance}%
                </Text>
            </div>
        </DashboardWidget>
    );
};

// Quick Actions Widget
export const QuickActionsWidget: React.FC<{
    onAction?: (action: string) => void;
}> = ({ onAction }) => {
    const handleAction = (action: string) => {
        notifications.show({
            title: 'Action Triggered',
            message: `${action} has been initiated`,
            color: 'blue'
        });
        onAction?.(action);
    };

    return (
        <DashboardWidget
            title="Quick Actions"
            icon={<IconGitBranch size={20} />}
        >
            <Stack gap="xs">
                <Button
                    variant="light"
                    leftSection={<IconPlayerPlay size={16} />}
                    onClick={() => handleAction('Start New Workflow')}
                    fullWidth
                >
                    Start New Workflow
                </Button>
                <Button
                    variant="light"
                    color="orange"
                    leftSection={<IconRefresh size={16} />}
                    onClick={() => handleAction('Restart Failed Jobs')}
                    fullWidth
                >
                    Restart Failed Jobs
                </Button>
                <Button
                    variant="light"
                    color="red"
                    leftSection={<IconAlertTriangle size={16} />}
                    onClick={() => handleAction('View System Alerts')}
                    fullWidth
                >
                    View System Alerts
                </Button>
                <Button
                    variant="light"
                    color="gray"
                    leftSection={<IconDatabase size={16} />}
                    onClick={() => handleAction('System Maintenance')}
                    fullWidth
                >
                    System Maintenance
                </Button>
            </Stack>
        </DashboardWidget>
    );
};

// Main Dashboard Layout Component
export const LifecycleDashboard: React.FC<{
    onNavigate?: (page: string) => void;
}> = ({ onNavigate }) => {
    const [refreshKey, setRefreshKey] = useState(0);

    // Auto-refresh every 30 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            setRefreshKey(prev => prev + 1);
        }, 30000);

        return () => clearInterval(interval);
    }, []);

    const handleNavigateToComponent = useCallback((componentType: string) => {
        onNavigate?.(componentType);
        notifications.show({
            title: 'Navigation',
            message: `Opening ${componentType} component`,
            color: 'blue'
        });
    }, [onNavigate]);

    return (
        <Grid>
            {/* Top Row - Overview and Health */}
            <Grid.Col span={6}>
                <WorkflowOverviewWidget 
                    onViewAll={() => handleNavigateToComponent('Instance')}
                    key={`overview-${refreshKey}`}
                />
            </Grid.Col>
            <Grid.Col span={6}>
                <SystemHealthWidget 
                    onViewDetails={() => handleNavigateToComponent('JobStatus')}
                    key={`health-${refreshKey}`}
                />
            </Grid.Col>

            {/* Middle Row - Jobs and Activity */}
            <Grid.Col span={6}>
                <ActiveJobsWidget 
                    onViewAll={() => handleNavigateToComponent('JobStatus')}
                    key={`jobs-${refreshKey}`}
                />
            </Grid.Col>
            <Grid.Col span={6}>
                <RecentActivityWidget 
                    onViewAll={() => handleNavigateToComponent('TimelineItem')}
                    key={`activity-${refreshKey}`}
                />
            </Grid.Col>

            {/* Bottom Row - Performance and Actions */}
            <Grid.Col span={8}>
                <PerformanceMetricsWidget 
                    onViewDetails={() => handleNavigateToComponent('ExecutionResult')}
                    key={`performance-${refreshKey}`}
                />
            </Grid.Col>
            <Grid.Col span={4}>
                <QuickActionsWidget 
                    onAction={(action) => console.log('Quick action:', action)}
                />
            </Grid.Col>
        </Grid>
    );
};

export default LifecycleDashboard;